/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2024] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import { View } from 'backbone.marionette';
import Backbone from 'backbone';

import { userHasRole } from '@/util/RoleUtils';

import NotificationApprovalGridView from '../../views/NotificationApprovalGridView';
import InProgressOrdersGridView from '../../views/InProgressOrdersGridView';
import InProgressAssessmentsGridView from '../../views/InProgressAssessmentsGridView';
import InProgressOrderPackagesGridView from '../../views/InProgressOrderPackagesGridView';
import InProgressAWPackagesGridView from '../../views/InProgressAWPackagesGridView';
import IntDashboardSidebarNavView from '../../views/IntDashboardSidebarNavView';
import RelocationSearchView from '../../views/RelocationSearchView';

import tmplIntDashboardPage from './IntDashboardPage.hbs';

const IntDashboardPage = View.extend({
  template: tmplIntDashboardPage,

  ui: {
    sideBar: '.sidebar-nav-container',
    notificationsApprovalsGridContainer: '#notificationsApprovalsGridContainer',
    inProgressOrdersGridContainer: '#inProgressOrdersGridContainer',
    inProgressAssessmentsGridContainer: '#inProgressAssessmentsGridContainer',
    inProgressOrderPackagesGridContainer: '#inProgressOrderPackagesGridContainer',
    inProgressAWPackagesGridContainer: '#inProgressAWPackagesGridContainer',
    relocationSearchContainer: '#relocationSearchContainer',
    searchOrdersBtn: '.btn-search-orders'
  },

  regions: {
    sideBar: '@ui.sideBar',
    notificationsApprovalsGridContainer: '@ui.notificationsApprovalsGridContainer',
    inProgressOrdersGridContainer: '@ui.inProgressOrdersGridContainer',
    inProgressAssessmentsGridContainer: '@ui.inProgressAssessmentsGridContainer',
    inProgressOrderPackagesGridContainer: '@ui.inProgressOrderPackagesGridContainer',
    inProgressAWPackagesGridContainer: '@ui.inProgressAWPackagesGridContainer',
    relocationSearchContainer: '@ui.relocationSearchContainer',
    searchOrdersBtn: '@ui.searchOrdersBtn'
  },

  events: {
    'click @ui.searchOrdersBtn': 'onClickToggleOrderSearch'
  },

  triggers: {
    'search:orders': 'search:orders'
  },

  childViewEvents: {
    'search:response': 'onOrderSearchResponse'
  },

  templateContext() {
    return {
      isCRSUser: userHasRole('CRS'),
      isEPCUser: userHasRole('EPC')
    };
  },

  onRender() {
    this.showChildView('sideBar', new IntDashboardSidebarNavView({ mode: 'dashboard' }));
    this.showNotificationsApprovalsGrid();

    const isCRSUser = userHasRole('CRS');
    const isEPCUser = userHasRole('EPC');

    if (isCRSUser) {
      this.showInProgressOrdersGrid();
      this.showInProgressAssessmentsGrid();
      this.showRelocationSearch();
      this.ui.relocationSearchContainer.hide();
      this.relocationSearchShown = false;
    }

    if (isCRSUser || isEPCUser) {
      this.showInProgressOrderPackagesGrid();
    }

    if (isEPCUser) {
      this.showInProgressAWPackagesGrid();
    }
  },

  showNotificationsApprovalsGrid() {
    this.showChildView(
      'notificationsApprovalsGridContainer',
      new NotificationApprovalGridView({
        model: new Backbone.Model({
          notificationsAndApprovals: this.model.get('notificationsAndApprovals')
        })
      })
    );
  },

  showInProgressOrdersGrid() {
    this.inProgressOrdersGrid = new InProgressOrdersGridView({
      model: new Backbone.Model({
        inProgressOrders: this.model.get('inProgressOrders')
      })
    });

    this.showChildView('inProgressOrdersGridContainer', this.inProgressOrdersGrid);
  },

  showInProgressAssessmentsGrid() {
    this.showChildView(
      'inProgressAssessmentsGridContainer',
      new InProgressAssessmentsGridView({
        model: new Backbone.Model({
          inProgressAssessments: this.model.get('inProgressAssessments')
        })
      })
    );
  },

  showInProgressOrderPackagesGrid() {
    this.showChildView(
      'inProgressOrderPackagesGridContainer',
      new InProgressOrderPackagesGridView({
        model: new Backbone.Model({
          inProgressOrderPackages: this.model.get('inProgressOrderPackages')
        })
      })
    );
  },

  showInProgressAWPackagesGrid() {
    this.showChildView(
      'inProgressAWPackagesGridContainer',
      new InProgressAWPackagesGridView({
        model: new Backbone.Model({
          inProgressAWPackages: this.model.get('inProgressAWPackages')
        })
      })
    );
  },

  showRelocationSearch() {
    this.showChildView(
      'relocationSearchContainer',
      new RelocationSearchView({
        model: new Backbone.Model({})
      })
    );
  },

  onClickToggleOrderSearch() {
    this.relocationSearchShown = !this.relocationSearchShown;
    this.relocationSearchShown ? this.ui.relocationSearchContainer.show() : this.ui.relocationSearchContainer.hide();
  },

  onOrderSearchResponse(response) {
    this.inProgressOrdersGrid.options.collection.fullCollection.reset(response.orders);
  }
});

export default IntDashboardPage;
